// IC1 — render the in-world pixel-art atlas sprites as crisp icons for the DOM menus (codex, character
// and map select, later HUD/cards), so the UI uses ORIGINAL pixel-art instead of cheap emoji. The pixel
// grid is built by a pure function (headless-testable); a thin browser helper bakes it to a data URL and
// an <img>. Same sprite definitions as the 3D atlas → the menu icon matches what you fight in-world.

import { SHAPES, SPRITES, Pal } from './pixelart'

const byName = new Map(SPRITES.map((s) => [s.name, s]))

// ---- IC2: weapon icon registry. Each weapon maps to a family silhouette (SHAPES) + a themed palette,
// so every weapon (base + evolved) gets a distinct ORIGINAL pixel-art icon. Kept out of the 3D atlas
// (SPRITES) — these are DOM-only menu icons resolved by the `wicon:<id>` name. ----
interface WIconDef {
  shape: string
  pal: Pal
}
/** Build an icon palette from a main colour (+ optional accent). AO1 HQ standard: a 5-step body ramp
 *  D (deep shadow) < B (shade) < A (mid) < H (highlight) < L (rim/lit edge), plus accent C + detail E. */
function wi(shape: string, main: string, accent?: string): WIconDef {
  const A = main
  return {
    shape,
    pal: {
      O: '#14121c',
      D: shade(A, 0.42),
      B: shade(A, 0.66),
      A,
      H: shade(A, 1.32),
      L: shade(A, 1.72),
      C: accent ?? shade(A, 1.55),
      E: accent ?? '#14121c',
    },
  }
}

const WEAPON_ICONS: Record<string, WIconDef> = {
  // base weapons
  boomeraxe: wi('wic_axe', '#d8843a'),
  revolver: wi('wic_gun', '#c6ccd8'),
  halo: wi('wic_ring', '#7ad7ff', '#ffe27a'),
  frostpulse: wi('wic_snow', '#7fe8ff'),
  meteor: wi('wic_meteor', '#ff7a2d'),
  zapper: wi('wic_bolt', '#ffe27a'),
  sawblade: wi('wic_saw', '#ff5a7a'),
  scattergun: wi('wic_gun', '#ffd23b'),
  drones: wi('wic_drone', '#c6ccd8', '#46e0ff'),
  tome: wi('wic_book', '#b98cff', '#ffd23b'),
  rotaura: wi('wic_swirl', '#9ed13b'),
  lash: wi('wic_whip', '#ffd23b'),
  caltrops: wi('wic_caltrop', '#c6ccd8'),
  ricochet: wi('wic_ball', '#46c8d8', '#ffffff'),
  singularity: wi('wic_hole', '#b46cff'),
  railgun: wi('wic_rail', '#aee5ff'),
  sporecloud: wi('wic_spore', '#9acd32'),
  frostlance: wi('wic_crystal', '#7fe8ff'),
  solarflare: wi('wic_sun', '#ffd23b'),
  novamortar: wi('wic_burst', '#ffe27a'),
  prism: wi('wic_prism', '#9fd0ff', '#ff6ad5'),
  quasarorb: wi('wic_orb', '#66ffff'),
  photonlance: wi('wic_beam', '#66ffff'),
  acidspit: wi('wic_drop', '#9acd32'),
  shocknova: wi('wic_shock', '#b46cff'),
  starshards: wi('wic_star', '#aee5ff'),
  cryobomb: wi('wic_crystal', '#9fe0ff'),
  plasmacutter: wi('wic_saw', '#ff8a3d'),
  // v11 base weapons
  needler: wi('wic_beam', '#d0e0ff'),
  warglaive: wi('wic_axe', '#aee5ff', '#ffffff'),
  flakcannon: wi('wic_burst', '#ffb14a'),
  razorring: wi('wic_saw', '#c6ccd8'),
  gustslash: wi('wic_whip', '#bfeaff'),
  // evolved weapons
  stormglaive: wi('wic_axe', '#6ad0ff', '#ffffff'),
  gatling: wi('wic_gun', '#ff7a3d'),
  ringofsaturn: wi('wic_ring', '#ffd27a', '#fff0a0'),
  blizzard: wi('wic_snow', '#bfe9ff'),
  apocalypse: wi('wic_meteor', '#ff3a2a', '#ffd23b'),
  tempest: wi('wic_bolt', '#a0f0ff'),
  pestilence: wi('wic_swirl', '#7ec850', '#a8e80c'),
  hydralash: wi('wic_whip', '#6ad06a'),
  minefield: wi('wic_caltrop', '#ff7a3d'),
  pinball: wi('wic_ball', '#ff6ad5', '#ffffff'),
  eventhorizon: wi('wic_hole', '#8a3aff', '#66ffff'),
  annihilator: wi('wic_rail', '#ff5a2d', '#ffd23b'),
  miasma: wi('wic_spore', '#88aa66', '#a8e80c'),
  glacialspear: wi('wic_crystal', '#cfe2ff'),
  coronalmass: wi('wic_sun', '#ff7a3d', '#ffe27a'),
  supernova: wi('wic_burst', '#ff8a3d', '#fff0a0'),
  spectrum: wi('wic_prism', '#ff6ad5', '#66ffff'),
  quasarstorm: wi('wic_orb', '#46e0ff', '#ffffff'),
  photonstorm: wi('wic_beam', '#fff0a0', '#ffffff'),
  acidstorm: wi('wic_drop', '#a8e80c'),
  pulsewave: wi('wic_shock', '#6ad0ff'),
  starfall: wi('wic_star', '#ffe27a'),
  absolutezero: wi('wic_crystal', '#eaffff', '#66ffff'),
  plasmavortex: wi('wic_saw', '#66ffff', '#ff8a3d'),
}

/** Atlas name for a weapon's pixel-art icon. */
export function weaponIconSprite(weaponId: string): string {
  return 'wicon:' + weaponId
}

// ---- IC3: item / tome / meta icons (same registry pattern, shared family silhouettes) ----
const ITEM_ICONS: Record<string, WIconDef> = {
  protein_shake: wi('iic_potion', '#ff9bb0'),
  energy_drink: wi('iic_potion', '#ffe27a'),
  running_shoes: wi('iic_boot', '#6ad0ff'),
  big_lunch: wi('iic_potion', '#ff7a3d'),
  magnet: wi('iic_magnet', '#d23a4a'),
  lucky_clover: wi('iic_clover', '#6ad06a'),
  spyglass: wi('iic_lens', '#c6ccd8'),
  wide_lens: wi('iic_lens', '#a0e0ff'),
  spring_coil: wi('wic_shock', '#c6ccd8'),
  cursed_doll: wi('wic_swirl', '#ff2e97'),
  leech_ring: wi('wic_ring', '#d23a4a'),
  iron_plate: wi('iic_shield', '#c6ccd8'),
  ghost_cloak: wi('iic_ghost', '#b0a0d0'),
  thorn_mail: wi('wic_caltrop', '#6ad06a'),
  piggy_bank: wi('iic_coin', '#ff9bb0'),
  graduation_cap: wi('iic_scroll', '#2f8fff'),
  phoenix_feather: wi('iic_wing', '#ff7a3d'),
  heart_locket: wi('iic_heart', '#ff3b5c'),
  gale_charm: wi('iic_wing', '#a0f0ff'),
  hot_sauce: wi('iic_potion', '#ff3a2a'),
  gyroscope: wi('iic_target', '#ffd23b'),
  frozen_heart: wi('iic_heart', '#7fe8ff'),
  ember_core: wi('wic_sun', '#ff7a3d'),
  capacitor: wi('iic_gear', '#ffe27a'),
  biohazard: wi('iic_potion', '#a8e80c'),
  barbed_wire: wi('wic_caltrop', '#c6ccd8'),
  gunpowder: wi('iic_bomb', '#ff5a2d'),
  rubber_ball: wi('wic_ball', '#6ad06a'),
  dark_matter: wi('wic_hole', '#b46cff'),
  tungsten_rod: wi('wic_rail', '#c6ccd8'),
  fungal_spores: wi('wic_spore', '#9acd32'),
  permafrost: wi('wic_snow', '#aee5ff'),
  solar_panel: wi('iic_gear', '#ffd23b'),
  fusion_core: wi('wic_sun', '#66ffff'),
  crystal_lens: wi('wic_crystal', '#66ffff'),
  event_shard: wi('wic_crystal', '#b46cff'),
  light_amp: wi('wic_sun', '#fff0a0'),
  acid_vial: wi('iic_potion', '#a8e80c'),
  kinetic_cell: wi('iic_gear', '#46e0ff'),
  shard_matrix: wi('wic_crystal', '#aee5ff'),
  cryo_canister: wi('iic_potion', '#7fe8ff'),
  plasma_cell: wi('iic_gear', '#ff8a3d'),
  overclock_chip: wi('iic_gear', '#ffe27a'),
  targeting_array: wi('iic_target', '#ff5a6a'),
  recoil_booster: wi('wic_burst', '#ff5a2d'),
  nano_coating: wi('iic_potion', '#c6ccd8'),
  focusing_array: wi('wic_beam', '#46e0ff'),
  adrenal_implant: wi('iic_potion', '#ff3a2a'),
  vampiric_fang: wi('iic_fang', '#d23a4a'),
  quantum_battery: wi('iic_gear', '#b46cff'),
  star_chart: wi('iic_scroll', '#aee5ff'),
  combat_stims: wi('iic_potion', '#ff5a6a'),
  reinforced_hull: wi('iic_shield', '#c6ccd8'),
  scavenger_drone: wi('wic_drone', '#c6ccd8'),
  warp_boots: wi('iic_boot', '#b46cff'),
  glass_reactor: wi('iic_potion', '#c0c6d0'),
  kinetic_barrier: wi('iic_shield', '#46e0ff'),
  ion_thrusters: wi('wic_sun', '#ff7a3d'),
  gravity_anchor: wi('iic_anchor', '#c6ccd8'),
  singularity_core: wi('wic_hole', '#b46cff'),
  volatile_rounds: wi('iic_bomb', '#ff7a3d'),
  static_discharge: wi('wic_bolt', '#ffe27a'),
  soul_harvest: wi('iic_ghost', '#b0a0d0'),
  critical_mass: wi('wic_burst', '#ff3a2a'),
  incinerator: wi('wic_sun', '#ff5a2d'),
  reapers_toll: wi('iic_fang', '#c6ccd8'),
  // v11 — shield defensive line
  aegis_capacitor: wi('iic_shield', '#6aa0e0'),
  barrier_field: wi('wic_orb', '#46e0ff'),
  deflector_array: wi('iic_shield', '#aee5ff'),
  overshield_core: wi('wic_orb', '#6ad0ff', '#ffffff'),
  cryo_plating: wi('iic_shield', '#7fe8ff'),
  // v11 — stat-hybrid passives
  titan_serum: wi('iic_potion', '#ff7a3d'),
  berserker_chip: wi('iic_gear', '#d23a4a'),
  predator_optics: wi('iic_lens', '#ff5a6a'),
  siege_battery: wi('wic_burst', '#ffae4a'),
  arc_reactor: wi('iic_gear', '#46e0ff'),
  blood_chalice: wi('iic_potion', '#b0203a'),
  windrunner_cloak: wi('iic_wing', '#bfeaff'),
  fortune_idol: wi('iic_coin', '#ffd23b'),
  neural_link: wi('iic_gear', '#9b6cff'),
  juggernaut_core: wi('iic_gear', '#9aa6b2'),
  venom_gland: wi('iic_potion', '#9acd32'),
  glass_lens: wi('wic_crystal', '#ff5a6a'),
  swarm_capacitor: wi('wic_beam', '#3fd6c0'),
  phoenix_core: wi('iic_wing', '#ff9f4a'),
  duelist_gloves: wi('iic_fist', '#ffd23b'),
  gravity_lens: wi('wic_hole', '#9b6cff'),
  prospector_lamp: wi('iic_coin', '#ffe27a'),
  warlords_banner: wi('wic_burst', '#ff7a3d'),
  sentinel_plating: wi('iic_shield', '#c6ccd8'),
  // v11 — proc / trigger items
  cryo_rounds: wi('wic_snow', '#7fe8ff'),
  soul_siphon: wi('wic_orb', '#46e0ff'),
  overcharger: wi('wic_bolt', '#ffe27a'),
  cluster_bombs: wi('iic_bomb', '#ff5a2d'),
  ember_rounds: wi('wic_sun', '#ff5a2d'),
}

const TOME_COLORS: Record<string, string> = {
  might: '#ff5a5a', vigor: '#6ad06a', haste: '#6ad0ff', frenzy: '#ffae4a', focus: '#ffd23b',
  ferocity: '#ff5a2d', reach: '#46e0ff', velocity: '#aee5ff', volley: '#b46cff', duration: '#c0c6d0',
  greed: '#ffd23b', scholar: '#9fd0ff', fortune: '#6ad06a', bulwark: '#c6ccd8', phantom: '#b0a0d0',
  magnetism: '#d23a4a', vitality: '#ff3b5c', leech: '#d23a4a',
  tempo: '#5fe6ff', force: '#ffae4a', thorns: '#6ad06a', affliction: '#9b5cff', rebirth: '#ffe9a0',
  onslaught: '#ff7a4a', aegis: '#c6ccd8', plunder: '#ffd23b',
}
const TOME_ICONS: Record<string, WIconDef> = Object.fromEntries(
  Object.entries(TOME_COLORS).map(([id, c]) => [id, wi('wic_book', c)]),
)

const META_ICONS: Record<string, WIconDef> = {
  might: wi('iic_fist', '#ff5a5a'),
  vigor: wi('iic_heart', '#ff3b5c'),
  haste: wi('iic_boot', '#6ad0ff'),
  frenzy: wi('iic_blade', '#ffae4a'),
  fortune: wi('iic_clover', '#6ad06a'),
  scholar: wi('iic_scroll', '#9fd0ff'),
  greed: wi('iic_coin', '#ffd23b'),
  plating: wi('iic_shield', '#c6ccd8'),
  magnetism: wi('iic_magnet', '#d23a4a'),
  precision: wi('iic_target', '#ff5a6a'),
  second_wind: wi('iic_wing', '#aee5ff'),
  // PT4 — new meta nodes
  ferocity: wi('cic_flame', '#ff5a2d', '#ffe27a'),
  reach: wi('cic_globe', '#6ad0ff', '#7fe8a0'),
  velocity: wi('wic_beam', '#aee5ff', '#ffffff'),
  vitality: wi('iic_heart', '#5be08a'),
  leech: wi('iic_fang', '#d23a4a'),
  volley: wi('wic_burst', '#b46cff'),
}

// ---- IC4: HUD/menu/toast CHROME glyphs. Fixed meaning-colours (no runtime tint). ----
const CHROME_ICONS: Record<string, WIconDef> = {
  gold: wi('coin', '#ffd23b', '#a06f10'),
  kills: wi('cic_skull', '#e6ecf4', '#8a93a8'),
  curse: wi('cic_skull', '#ff5a9a', '#a01060'),
  evolve: wi('cic_spark', '#ffe27a', '#fff6c0'),
  spark: wi('cic_spark', '#ffe27a', '#fff6c0'),
  lock: wi('cic_lock', '#c6ccd8', '#ffd23b'),
  flame: wi('cic_flame', '#ff7a2d', '#ffe27a'),
  heal: wi('cic_heal', '#e8a060', '#ff6a6a'),
  play: wi('cic_play', '#5be08a', '#bfffd8'),
  store: wi('cic_store', '#9fd0ff', '#ff5a6a'),
  stats: wi('cic_chart', '#6ad0ff', '#ffd23b'),
  trophy: wi('cic_trophy', '#ffd23b', '#fff0a0'),
  achievements: wi('cic_trophy', '#ffd23b', '#fff0a0'),
  globe: wi('cic_globe', '#6ad0ff', '#7fe8a0'),
  banish: wi('cic_banish', '#ff5a5a', '#ffffff'),
  pin: wi('cic_pin', '#ff5a6a', '#ffd23b'),
  check: wi('cic_check', '#5be08a', '#bfffd8'),
  gamepad: wi('cic_gamepad', '#c6ccd8', '#ff5a6a'),
  settings: wi('iic_gear', '#c6ccd8', '#9aa3b4'),
  challenges: wi('iic_target', '#ff6a8a', '#ffd23b'),
  codex: wi('wic_book', '#b98cff', '#ffd23b'),
  install: wi('cic_install', '#5be08a', '#bfffd8'),
}

export function chromeIconSprite(key: string): string {
  return 'cicon:' + key
}

// ---- IC5: achievement + challenge icons (reuse the established shapes, themed per goal) ----
const ACHIEVEMENT_ICONS: Record<string, WIconDef> = {
  first_blood: wi('wic_drop', '#d23a4a'),
  centurion: wi('blade', '#c6ccd8'),
  slayer: wi('cic_skull', '#e6ecf4'),
  exterminator: wi('cic_skull', '#ff5a5a'),
  survivor: wi('cic_clock', '#9fd0ff', '#ffd23b'),
  marathoner: wi('iic_boot', '#6ad0ff'),
  level10: wi('star', '#aee5ff'),
  level20: wi('iic_fist', '#ff9f4a'),
  level30: wi('wic_burst', '#ffe27a'),
  boss1: wi('blade', '#ff5a7a'),
  boss10: wi('wic_beam', '#9acd32'),
  boss25: wi('cic_trophy', '#ff7a3d'),
  clear_meadow: wi('tree', '#6fc04a'),
  clear_dunes: wi('cactus', '#7fd060'),
  clear_graveyard: wi('grave', '#c4ccd4'),
  clear_rift: wi('wic_hole', '#b46cff'),
  world_tour: wi('cic_globe', '#6ad0ff', '#7fe8a0'),
  hard_clear: wi('cic_flame', '#ff7a2d', '#ffe27a'),
  cooked_clear: wi('cic_flame', '#ff3a2a', '#ffd23b'),
  rich: wi('coin', '#ffd23b', '#a06f10'),
  tycoon: wi('coin', '#ffe27a', '#c89010'),
  first_evo: wi('cic_spark', '#ffe27a'),
  evo_master: wi('wic_burst', '#b46cff'),
  recruiter: wi('astronaut', '#6ad0ff'),
  full_roster: wi('cic_crown', '#ffd23b', '#fff0a0'),
  regular: wi('cic_gamepad', '#c6ccd8', '#ff5a6a'),
  addicted: wi('cic_gamepad', '#9fd0ff', '#ffd23b'),
  level40: wi('wic_burst', '#ffe27a'),
  kills25k: wi('wic_burst', '#ff3a2a'),
  boss50: wi('cic_crown', '#ffd23b', '#fff0a0'),
  veteran: wi('cic_trophy', '#ffd23b', '#fff0a0'),
}

const CHALLENGE_ICONS: Record<string, WIconDef> = {
  double_bosses: wi('cic_trophy', '#ff5a7a'),
  glass_cannon: wi('wic_burst', '#ff5a2d'),
  swarm: wi('slimesmall', '#e08a4a'),
  cursed_start: wi('cic_skull', '#ff5a9a', '#a01060'),
  iron_man: wi('iic_shield', '#c6ccd8', '#ffd23b'),
  speed_demon: wi('bolt', '#ffe27a'),
}

export function achievementIconSprite(id: string): string {
  return 'aicon:' + id
}
export function challengeIconSprite(id: string): string {
  return 'chicon:' + id
}

export function itemIconSprite(id: string): string {
  return 'iicon:' + id
}
export function tomeIconSprite(id: string): string {
  return 'ticon:' + id
}
export function metaIconSprite(id: string): string {
  return 'micon:' + id
}

function clampByte(n: number): number {
  return n < 0 ? 0 : n > 255 ? 255 : Math.round(n)
}

/** Multiply a #rrggbb colour by a factor (───>1 lightens, <1 darkens), clamped. */
export function shade(hex: string, f: number): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex)
  if (!m) return hex
  const v = parseInt(m[1], 16)
  const r = clampByte(((v >> 16) & 0xff) * f)
  const g = clampByte(((v >> 8) & 0xff) * f)
  const b = clampByte((v & 0xff) * f)
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')
}

/** #rrggbb for a numeric colour. */
export function hexColor(n: number): string {
  return '#' + (n >>> 0).toString(16).padStart(6, '0').slice(-6)
}

export interface PixelGrid {
  w: number
  h: number
  /** row-major; each cell is a #rrggbb colour or null (transparent). */
  cells: (string | null)[][]
}

/**
 * Resolve a sprite to a concrete pixel grid. Pure: no canvas, no THREE — testable headlessly.
 * `tint` (a numeric colour) recolours the body (A/B/H) so one base shape yields per-entity variants
 * (e.g. each character's astronaut suit), leaving detail keys (eyes/visor/accent) intact.
 */
export function spritePixelRows(name: string, tint?: number): PixelGrid | null {
  let shapeKey: string
  let basePal: Pal
  const reg =
    name.startsWith('wicon:') ? WEAPON_ICONS[name.slice(6)] :
    name.startsWith('iicon:') ? ITEM_ICONS[name.slice(6)] :
    name.startsWith('ticon:') ? TOME_ICONS[name.slice(6)] :
    name.startsWith('micon:') ? META_ICONS[name.slice(6)] :
    name.startsWith('cicon:') ? CHROME_ICONS[name.slice(6)] :
    name.startsWith('aicon:') ? ACHIEVEMENT_ICONS[name.slice(6)] :
    name.startsWith('chicon:') ? CHALLENGE_ICONS[name.slice(7)] :
    undefined
  if (reg !== undefined || name.includes('icon:')) {
    if (!reg) return null
    shapeKey = reg.shape
    basePal = reg.pal
  } else {
    const entry = byName.get(name)
    if (!entry) return null
    shapeKey = entry.shape
    basePal = entry.pal
  }
  const shape = SHAPES[shapeKey]
  if (!shape) return null
  let pal: Pal = basePal
  if (tint !== undefined) {
    const base = hexColor(tint)
    pal = { ...basePal, A: base, B: shade(base, 0.62), H: shade(base, 1.35) }
  }
  const h = shape.length
  const w = Math.max(...shape.map((r) => r.length))
  const cells: (string | null)[][] = []
  for (let r = 0; r < h; r++) {
    const row = shape[r]
    const out: (string | null)[] = []
    for (let c = 0; c < w; c++) {
      const key = row[c]
      out.push(key && key !== ' ' && key !== '.' && pal[key] ? pal[key] : null)
    }
    cells.push(out)
  }
  return { w, h, cells }
}

// ---- browser-only: bake a grid into a crisp data URL, cached by (name|tint) ----
const urlCache = new Map<string, string>()

export function spriteDataURL(name: string, tint?: number): string {
  if (typeof document === 'undefined') return '' // headless / tests
  const key = name + (tint === undefined ? '' : '|' + tint)
  const cached = urlCache.get(key)
  if (cached !== undefined) return cached
  const grid = spritePixelRows(name, tint)
  if (!grid) {
    urlCache.set(key, '')
    return ''
  }
  const canvas = document.createElement('canvas')
  canvas.width = grid.w
  canvas.height = grid.h
  const ctx = canvas.getContext('2d')!
  for (let r = 0; r < grid.h; r++) {
    for (let c = 0; c < grid.w; c++) {
      const col = grid.cells[r][c]
      if (!col) continue
      ctx.fillStyle = col
      ctx.fillRect(c, r, 1, 1)
    }
  }
  const url = canvas.toDataURL()
  urlCache.set(key, url)
  return url
}

// A representative biome sprite per map, so map-select shows the world's flavour in pixel-art.
const MAP_ICON: Record<string, string> = {
  meadow: 'prop:moonrock',
  dunes: 'prop:cactus',
  graveyard: 'prop:grave',
  rift: 'prop:crystal',
}
export function mapIconSprite(mapId: string): string {
  return MAP_ICON[mapId] ?? 'prop:moonrock'
}

/**
 * An <img> tag (pixelated) for a sprite, ready to drop into a menu template. Falls back to a supplied
 * emoji/text when the sprite is unknown or we're headless, so nothing renders blank.
 */
export function iconHTML(name: string, opts: { size?: number; tint?: number; cls?: string; fallback?: string } = {}): string {
  const url = spriteDataURL(name, opts.tint)
  const size = opts.size ?? 44
  const cls = 'px-ico' + (opts.cls ? ' ' + opts.cls : '')
  if (!url) return opts.fallback ?? ''
  return `<img class="${cls}" width="${size}" height="${size}" src="${url}" alt="" draggable="false">`
}

/** Inline chrome glyph (gold/skull/lock/flame/play…) for HUD/menu/toast text — small by default. */
export function chromeIconHTML(key: string, size = 18, fallback = ''): string {
  return iconHTML(chromeIconSprite(key), { size, cls: 'px-chrome', fallback })
}
